import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  InputAdornment,
  Link,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { sharedAssignedClientFromClientProfile } from '@/components/questionnaires/json-driven/clientContext';
import { getClientById } from '@/lib/api/clients';
import {
  getQuestionnaireById,
  getQuestionnaireResponsesById,
  sendQuestionnaireReminder,
  type GetQuestionnaireResponsesByIdResponse,
  type QuestionnaireInstanceStatus,
  type QuestionnaireResponseByQuestionnaireItem,
} from '@/lib/api/questionnaires';
import { getCurrentUser } from '@/lib/api/auth';
import { useDebounce } from '@/hooks/useDebounce';
import { extractErrorMessage, showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import { QuestionnaireManualFillDialog } from './questionnaire-final-detail/sections';
import { ActionTour, type ActionTourStep } from '@/components/tutorial/ActionTour';
import notebookIcon from '@/assets/images/notebook-new.svg';
import clockCircleIcon from '@/assets/images/clock-circle-new.svg';
import usersGroupRoundedIcon from '@/assets/images/users-group-rounded.svg';
import verifiedCheckIcon from '@/assets/images/verified-check.svg';
import { ClientNameLink } from '@/components/clients/ClientNameLink';

type ResponseStatus = QuestionnaireInstanceStatus;

interface ResponseRow {
  id: string;
  clientId: string;
  fullName: string;
  conditionsText: string;
  activityLabel: string;
  submittedOn: string;
  status: ResponseStatus;
}

const pageTitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: { xs: '26px', md: '32px' },
  lineHeight: { xs: '115%', md: '100%' },
  letterSpacing: 0,
  color: '#1e293b',
} as const;

const tableHeaderCellSx = {
  borderBottomColor: '#edf2f7',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '0.6px',
  color: '#0A0A0A',
} as const;

const tableHeaderCenterCellSx = {
  ...tableHeaderCellSx,
  textAlign: 'center',
} as const;

const tableBodyCenterCellSx = {
  borderBottomColor: '#edf2f7',
  verticalAlign: 'middle',
  textAlign: 'center',
} as const;

const centeredCellContentSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
} as const;

const questionnaireResponsesActionTourSteps: ActionTourStep[] = [
  {
    id: 'questionnaire-responses-search',
    title: 'Find a Client Response',
    description: 'Search clients on this questionnaire response page when you need one client status or submission quickly.',
  },
  {
    id: 'questionnaire-responses-copy-link',
    title: 'Copy Share Link',
    description: 'Copy Link gives you the client questionnaire URL. Share that link with the client when they need to complete the questionnaire themselves.',
  },
  {
    id: 'questionnaire-responses-fill-manually',
    title: 'Fill Manually',
    description: 'Fill Manually opens the questionnaire in manual mode so the practitioner can enter answers for the client during a call or appointment.',
  },
  {
    id: 'questionnaire-responses-row-action',
    title: 'View or Remind',
    description: 'Completed rows show View so you can inspect submitted answers. Pending or in-progress rows show Send Reminder to email the client again.',
  },
];

const tableOutlineButtonSx = {
  height: 40,
  borderRadius: '12px',
  borderColor: '#cfd8e3',
  color: '#0f172a',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '13px',
  lineHeight: '18px',
  '&:hover': {
    borderColor: '#c0cbd8',
    bgcolor: '#f8fafc',
  },
  '&.Mui-disabled': {
    borderColor: '#e5e7eb',
    color: '#98a2b3',
    bgcolor: '#f8fafc',
  },
} as const;

function normalizeResponseStatus(
  status: QuestionnaireResponseByQuestionnaireItem['status']
): ResponseStatus {
  if (status === 'completed' || status === 'in_progress' || status === 'pending') {
    return status;
  }
  return 'pending';
}

function buildResponseRowsFromApi(responses: QuestionnaireResponseByQuestionnaireItem[]): ResponseRow[] {
  return responses.map((r) => {
    const fullName = [r.firstName, r.lastName].filter(Boolean).join(' ').trim() || r.email || '—';
    let sentLabel: string | null = null;
    if (r.sentAt) {
      const d = new Date(r.sentAt);
      if (!Number.isNaN(d.getTime())) {
        sentLabel = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(d);
      }
    }
    return {
      id: r.instanceId,
      clientId: r.clientId,
      fullName,
      conditionsText: r.email || '—',
      activityLabel: sentLabel ? `Sent: ${sentLabel}` : 'Sent: —',
      submittedOn: formatResponseDate(r.submittedOn),
      status: normalizeResponseStatus(r.status),
    };
  });
}

function markQuestionnaireResponseInProgress(
  current: GetQuestionnaireResponsesByIdResponse | undefined,
  instanceId: string
): GetQuestionnaireResponsesByIdResponse | undefined {
  if (!current) return current;

  let didUpdate = false;
  const responses = current.responses.map((response) => {
    if (response.instanceId !== instanceId) return response;
    if (response.status === 'in_progress') return response;

    didUpdate = true;
    return {
      ...response,
      status: 'in_progress' as const,
    };
  });

  return didUpdate ? { ...current, responses } : current;
}

function formatResponseDate(value?: string | null): string {
  if (!value) return '--/--/----';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--/--/----';
  const day = date.getDate();
  const suffix =
    day >= 11 && day <= 13 ? 'th' : day % 10 === 1 ? 'st' : day % 10 === 2 ? 'nd' : day % 10 === 3 ? 'rd' : 'th';
  const month = new Intl.DateTimeFormat('en-GB', { month: 'short' }).format(date);
  const year = date.getFullYear();
  return `${day}${suffix} ${month} ${year}`;
}

function buildQuestionnaireShareLink(instanceId: string, mode?: 'manual'): string {
  const link = `${window.location.origin}/questionnaire/share/${encodeURIComponent(instanceId)}`;
  return mode === 'manual' ? `${link}?mode=manual` : link;
}

function SummaryMetric({
  icon,
  value,
  label,
  iconAlt,
}: {
  icon: string;
  value: string | number;
  label: string;
  iconAlt: string;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box component="img" src={icon} alt={iconAlt} sx={{ width: 16, height: 16, display: 'block' }} />
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '16px',
          color: '#667085',
        }}
      >
        {value} {label}
      </Typography>
    </Box>
  );
}

function getStatusPillStyle(status: ResponseStatus): { label: string; bgcolor: string } {
  switch (status) {
    case 'completed':
      return { label: 'Completed', bgcolor: '#34c4bb' };
    case 'in_progress':
      return { label: 'In Progress', bgcolor: '#f4a40d' };
    case 'pending':
      return { label: 'Pending', bgcolor: '#94a3b8' };
  }
}

function StatusPill({ status }: { status: ResponseStatus }) {
  const { label, bgcolor } = getStatusPillStyle(status);

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.75,
        py: 0.75,
        borderRadius: '999px',
        bgcolor,
        color: '#fff',
      }}
    >
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: '#fff',
          opacity: 0.9,
        }}
      />
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '16px',
          color: '#fff',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

const QuestionnaireResponsesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  const user = getCurrentUser();
  const nutritionistId = user?.id;
  const [searchQuery, setSearchQuery] = React.useState('');
  const [manualFillRow, setManualFillRow] = React.useState<ResponseRow | null>(null);
  const [remindingInstanceId, setRemindingInstanceId] = React.useState<string | null>(null);
  const debouncedSearch = useDebounce(searchQuery, 300);

  const {
    data: section,
    isLoading: isQuestionnaireLoading,
    isError: isQuestionnaireError,
    error: questionnaireError,
  } = useQuery({
    queryKey: ['questionnaire-responses', questionnaireId, nutritionistId],
    queryFn: () => getQuestionnaireById(questionnaireId!, nutritionistId),
    enabled: Boolean(questionnaireId && nutritionistId),
  });

  const {
    data: responsesData,
    isLoading: isResponsesLoading,
  } = useQuery({
    queryKey: ['questionnaire-responses-by-id', questionnaireId],
    queryFn: () => getQuestionnaireResponsesById(questionnaireId!),
    enabled: Boolean(questionnaireId),
  });

  const manualFillClientId = manualFillRow?.clientId;
  const { data: manualFillClientDetails } = useQuery({
    queryKey: ['clientById', manualFillClientId, 'questionnaire-manual-fill'],
    queryFn: () => getClientById(manualFillClientId as string),
    enabled: Boolean(manualFillClientId),
    staleTime: 60_000,
  });

  const allRows = React.useMemo(() => {
    if (!responsesData?.responses) return [];
    return buildResponseRowsFromApi(responsesData.responses);
  }, [responsesData]);

  const visibleRows = React.useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter(
      (row) =>
        row.fullName.toLowerCase().includes(q) || row.conditionsText.toLowerCase().includes(q)
    );
  }, [allRows, debouncedSearch]);

  const handleViewResponse = (row: ResponseRow) => {
    if (!questionnaireId || !row.clientId) {
      showWarningToast('Cannot open response', 'Missing questionnaire or client identifier for this response.');
      return;
    }

    const query = new URLSearchParams({
      clientId: row.clientId,
      clientName: row.fullName,
    });
    navigate(`/questionnaires/${encodeURIComponent(questionnaireId)}/responses/${encodeURIComponent(row.id)}?${query.toString()}`);
  };

  const handleSendReminder = async (row: ResponseRow) => {
    if (!row.id || remindingInstanceId) return;

    setRemindingInstanceId(row.id);
    try {
      await sendQuestionnaireReminder(row.id);
      showSuccessToast('Reminder email sent', `Reminder sent to ${row.fullName}.`);
    } catch (error) {
      console.error('Failed to send questionnaire reminder', error);
      showErrorToast(error, extractErrorMessage(error) || 'Could not send reminder. Please try again.');
    } finally {
      setRemindingInstanceId(null);
    }
  };

  const handleCopyQuestionnaireLink = async (row: ResponseRow) => {
    if (!row.id) {
      showWarningToast('Cannot copy link', 'Missing questionnaire assignment identifier.');
      return;
    }

    const shareLink = buildQuestionnaireShareLink(row.id);

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareLink);
        showSuccessToast('Link copied', `Questionnaire link copied for ${row.fullName}.`);
        return;
      }

      window.prompt('Copy questionnaire link', shareLink);
      showSuccessToast('Link generated', 'Clipboard is unavailable, so the link is ready to copy.');
    } catch (error) {
      showErrorToast(error, 'Unable to copy questionnaire link');
    }
  };

  const handleFillManually = (row: ResponseRow) => {
    if (!row.id) {
      showWarningToast('Cannot open questionnaire', 'Missing questionnaire assignment identifier.');
      return;
    }

    setManualFillRow(row);
  };

  const handleManualFillSubmitted = React.useCallback(
    (instanceId: string) => {
      void queryClient.invalidateQueries({
        queryKey: ['questionnaire-responses-by-id', questionnaireId],
      });

      if (!questionnaireId || !manualFillRow?.clientId) return;

      const query = new URLSearchParams({
        clientId: manualFillRow.clientId,
        clientName: manualFillRow.fullName,
      });
      navigate(
        `/questionnaires/${encodeURIComponent(questionnaireId)}/responses/${encodeURIComponent(instanceId)}?${query.toString()}`
      );
    },
    [manualFillRow?.clientId, navigate, questionnaireId, queryClient]
  );

  const handleManualFillDraftSaved = React.useCallback(
    (instanceId: string) => {
      queryClient.setQueryData<GetQuestionnaireResponsesByIdResponse>(
        ['questionnaire-responses-by-id', questionnaireId],
        (current) => markQuestionnaireResponseInProgress(current, instanceId)
      );
      void queryClient.invalidateQueries({
        queryKey: ['questionnaire-responses-by-id', questionnaireId],
      });
      if (manualFillRow?.clientId) {
        void queryClient.invalidateQueries({
          queryKey: ['questionnaireResponses', manualFillRow.clientId],
        });
      }
    },
    [manualFillRow?.clientId, questionnaireId, queryClient]
  );

  if (isQuestionnaireLoading) {
    return (
      <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
        <Paper
          elevation={0}
          sx={{
            minHeight: 'calc(100vh - 7.5rem)',
            overflow: 'hidden',
            borderRadius: '28px',
            border: '1px solid #dbe6eb',
            bgcolor: '#fff',
            p: { xs: 3, md: 4 },
          }}
        >
          <Skeleton variant="rounded" height={34} width={280} sx={{ borderRadius: '12px' }} />
          <Skeleton variant="rounded" height={154} sx={{ mt: 3, borderRadius: '24px' }} />
          <Skeleton variant="rounded" height={560} sx={{ mt: 3, borderRadius: '24px' }} />
        </Paper>
      </Box>
    );
  }

  if (isQuestionnaireError || !section) {
    return (
      <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
        <Paper
          elevation={0}
          sx={{
            minHeight: 'calc(100vh - 7.5rem)',
            borderRadius: '28px',
            border: '1px solid #dbe6eb',
            bgcolor: '#fff',
            p: { xs: 3, md: 4 },
          }}
        >
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '26px', color: '#0f172a' }}>
            Questionnaire not available
          </Typography>
          <Typography sx={{ mt: 1.5, fontFamily: 'Arial, sans-serif', fontSize: '16px', color: '#667085' }}>
            {(questionnaireError as Error | undefined)?.message ?? 'The responses screen could not be loaded.'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/questionnaires')}
            sx={{
              mt: 3,
              borderRadius: '14px',
              bgcolor: '#25a8da',
              fontFamily: 'Inter, sans-serif',
              '&:hover': { bgcolor: '#1498ca' },
            }}
          >
            Back to Questionnaires
          </Button>
        </Paper>
      </Box>
    );
  }

  const questionCount = responsesData?.questionsCount ?? 0;
  const assignedClientsCount = responsesData?.assignedClientsCount ?? 0;
  const responsesCount = responsesData?.responsesCount ?? 0;
  const durationLabel =
    responsesData?.estimatedMinutes != null && responsesData.estimatedMinutes > 0
      ? `${responsesData.estimatedMinutes} min`
      : '—';

  return (
    <Box data-demo-id="questionnaire-responses-page" sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid #dbe6eb',
          bgcolor: '#fff',
        }}
      >
        <Box sx={{ px: { xs: 3, md: 4 }, pt: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: '1px solid #dbe6eb',
              bgcolor: '#fafcfe',
              px: 3,
              py: 1.5,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Link
                component={RouterLink}
                underline="hover"
                color="inherit"
                to="/questionnaires"
                sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}
              >
                Questionnaires
              </Link>
            </Breadcrumbs>
          </Paper>
        </Box>

        <Box sx={{ px: { xs: 3, md: 4 }, pb: { xs: 4, md: 5 }, pt: 4 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '22px',
              border: '1px solid #dbe6eb',
              bgcolor: '#fff',
              px: { xs: 2.5, md: 3 },
              py: { xs: 2.5, md: 3 },
            }}
          >
            <Typography sx={pageTitleSx}>{section.title}</Typography>
            <Typography
              sx={{
                mt: 1.25,
                fontFamily: 'Arial, sans-serif',
                fontSize: '16px',
                lineHeight: '24px',
                color: '#667085',
              }}
            >
              Manage and assign questionnaire to your clients
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mt: 2 }}>
              <SummaryMetric icon={notebookIcon} iconAlt="Questions" value={questionCount} label="questions" />
              <SummaryMetric icon={clockCircleIcon} iconAlt="Duration" value={durationLabel} label="" />
              <SummaryMetric icon={usersGroupRoundedIcon} iconAlt="Assigned" value={assignedClientsCount} label="assigned" />
              <SummaryMetric icon={verifiedCheckIcon} iconAlt="Responses" value={responsesCount} label="responses" />
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mt: 3,
              overflow: 'hidden',
              borderRadius: '22px',
              border: '1px solid #dbe6eb',
              bgcolor: '#fff',
            }}
          >
            <Box sx={{ px: { xs: 2, md: 3 }, py: 2, borderBottom: '1px solid #edf2f7' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'flex-start', md: 'center' },
                  gap: { xs: 1.25, md: 2.25 },
                }}
              >
                <TextField
                  data-action-tour-id="questionnaire-responses-search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search clients..."
                  size="small"
                  sx={{
                    width: { xs: '100%', md: 420, lg: 450 },
                    minWidth: { xs: '100%', md: 420, lg: 450 },
                    '& .MuiOutlinedInput-root': {
                      height: 48,
                      borderRadius: '14px',
                      bgcolor: '#fff',
                      '& fieldset': { borderColor: '#dbe6eb' },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ color: '#98a2b3' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: { xs: 1180, md: 1380 } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={tableHeaderCellSx}>Client</TableCell>
                    <TableCell sx={{ ...tableHeaderCenterCellSx, width: { xs: 190, md: 260 } }}>
                      Submitted On
                    </TableCell>
                    <TableCell sx={{ ...tableHeaderCenterCellSx, width: { xs: 150, md: 220 } }}>Status</TableCell>
                    <TableCell sx={{ ...tableHeaderCenterCellSx, width: { xs: 150, md: 190 } }}>
                      Copy Link
                    </TableCell>
                    <TableCell sx={{ ...tableHeaderCenterCellSx, width: { xs: 160, md: 210 } }}>
                      Fill Manually
                    </TableCell>
                    <TableCell sx={{ ...tableHeaderCenterCellSx, width: { xs: 170, md: 240 } }}>Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isResponsesLoading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <TableRow key={`responses-loading-${index}`}>
                        <TableCell sx={{ borderBottomColor: '#edf2f7' }}>
                          <Skeleton variant="rounded" width={240} height={54} />
                        </TableCell>
                        <TableCell sx={tableBodyCenterCellSx}>
                          <Box sx={centeredCellContentSx}>
                            <Skeleton variant="rounded" width={148} height={36} />
                          </Box>
                        </TableCell>
                        <TableCell sx={tableBodyCenterCellSx}>
                          <Box sx={centeredCellContentSx}>
                            <Skeleton variant="rounded" width={110} height={30} />
                          </Box>
                        </TableCell>
                        <TableCell sx={tableBodyCenterCellSx}>
                          <Box sx={centeredCellContentSx}>
                            <Skeleton variant="rounded" width={132} height={40} />
                          </Box>
                        </TableCell>
                        <TableCell sx={tableBodyCenterCellSx}>
                          <Box sx={centeredCellContentSx}>
                            <Skeleton variant="rounded" width={144} height={40} />
                          </Box>
                        </TableCell>
                        <TableCell sx={tableBodyCenterCellSx}>
                          <Box sx={centeredCellContentSx}>
                            <Skeleton variant="rounded" width={160} height={40} />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : visibleRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 8, borderBottomColor: '#edf2f7', textAlign: 'center' }}>
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            fontSize: '20px',
                            color: '#0f172a',
                          }}
                        >
                          {allRows.length === 0
                            ? 'No responses yet'
                            : 'No clients match this search'}
                        </Typography>
                        <Typography sx={{ mt: 1, fontFamily: 'Arial, sans-serif', fontSize: '15px', color: '#667085' }}>
                          {allRows.length === 0
                            ? 'Responses will appear here once this questionnaire has been sent to clients.'
                            : 'Try another search term or clear the search to see questionnaire responses.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleRows.map((row) => {
                      const isCompleted = row.status === 'completed';

                      return (
                        <TableRow
                          key={row.id}
                          sx={{
                            bgcolor: '#fff',
                            '&.MuiTableRow-hover:hover': { bgcolor: '#fff' },
                            '&:last-of-type td': { borderBottom: 'none' },
                            '& td': { borderBottomColor: '#edf2f7', verticalAlign: 'middle' },
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                              <Box
                                sx={{
                                  width: 38,
                                  height: 38,
                                  borderRadius: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: '#e8f1ff',
                                  color: '#3b82f6',
                                  flexShrink: 0,
                                }}
                              >
                                <PersonRoundedIcon sx={{ fontSize: 20 }} />
                              </Box>

                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: 500,
                                    fontSize: '15px',
                                    lineHeight: '22px',
                                    color: '#334155',
                                  }}
                                >
                                  <ClientNameLink clientId={row.clientId} className="text-[#334155] hover:text-[#334155]">
                                    {row.fullName}
                                  </ClientNameLink>
                                </Typography>
                                <Typography
                                  sx={{
                                    mt: 0.25,
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    color: '#667085',
                                  }}
                                >
                                  {row.conditionsText}
                                </Typography>
                                <Typography
                                  sx={{
                                    mt: 0.25,
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '13px',
                                    lineHeight: '18px',
                                    color: '#98a2b3',
                                  }}
                                >
                                  {row.activityLabel}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell sx={tableBodyCenterCellSx}>
                            <Box sx={centeredCellContentSx}>
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  minWidth: { xs: 120, md: 148 },
                                  minHeight: 38,
                                  px: 2,
                                  borderRadius: '12px',
                                  border: '1px solid #e5edf5',
                                  bgcolor: '#f8fafc',
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    color: row.submittedOn === '--/--/----' ? '#667085' : '#0f172a',
                                  }}
                                >
                                  {row.submittedOn}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell sx={tableBodyCenterCellSx}>
                            <Box sx={centeredCellContentSx}>
                              <StatusPill status={row.status} />
                            </Box>
                          </TableCell>

                          <TableCell sx={tableBodyCenterCellSx}>
                            <Box sx={centeredCellContentSx}>
                              <Button
                                data-action-tour-id="questionnaire-responses-copy-link"
                                variant="outlined"
                                disabled={isCompleted}
                                onClick={() => handleCopyQuestionnaireLink(row)}
                                sx={{
                                  ...tableOutlineButtonSx,
                                  minWidth: { xs: 120, md: 136 },
                                }}
                              >
                                Copy Link
                              </Button>
                            </Box>
                          </TableCell>

                          <TableCell sx={tableBodyCenterCellSx}>
                            <Box sx={centeredCellContentSx}>
                              <Button
                                data-action-tour-id="questionnaire-responses-fill-manually"
                                variant="outlined"
                                disabled={isCompleted}
                                onClick={() => handleFillManually(row)}
                                sx={{
                                  ...tableOutlineButtonSx,
                                  minWidth: { xs: 132, md: 152 },
                                }}
                              >
                                Fill Manually
                              </Button>
                            </Box>
                          </TableCell>

                          <TableCell sx={tableBodyCenterCellSx}>
                            <Box sx={centeredCellContentSx}>
                              {isCompleted ? (
                                <Button
                                  data-action-tour-id="questionnaire-responses-row-action"
                                  variant="contained"
                                  onClick={() => handleViewResponse(row)}
                                sx={{
                                  minWidth: { xs: 120, md: 168 },
                                  height: 40,
                                  borderRadius: '12px',
                                  bgcolor: '#2fa8d5',
                                  color: '#fff',
                                  fontFamily: 'Inter, sans-serif',
                                  fontWeight: 500,
                                  fontSize: '13px',
                                  lineHeight: '18px',
                                  boxShadow: 'none',
                                  '&:hover': {
                                    bgcolor: '#1498ca',
                                    boxShadow: 'none',
                                  },
                                }}
                              >
                                  View
                                </Button>
                              ) : (
                                <Button
                                  data-action-tour-id="questionnaire-responses-row-action"
                                  variant="outlined"
                                  disabled={remindingInstanceId === row.id}
                                  onClick={() => void handleSendReminder(row)}
                                sx={{
                                  minWidth: { xs: 120, md: 168 },
                                  height: 40,
                                  borderRadius: '12px',
                                  borderColor: '#cfd8e3',
                                  color: '#0f172a',
                                  fontFamily: 'Inter, sans-serif',
                                  fontWeight: 500,
                                  fontSize: '13px',
                                  lineHeight: '18px',
                                  '&:hover': {
                                    borderColor: '#c0cbd8',
                                    bgcolor: '#f8fafc',
                                  },
                                }}
                              >
                                  {remindingInstanceId === row.id ? 'Sending…' : 'Send Reminder'}
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Paper>

      <QuestionnaireManualFillDialog
        open={Boolean(manualFillRow)}
        shareId={manualFillRow?.id ?? null}
        questionnaireTitle={section.title || 'Questionnaire'}
        clientName={manualFillRow?.fullName || 'Selected client'}
        assignedClientProfile={
          manualFillClientDetails?.client
            ? sharedAssignedClientFromClientProfile(manualFillClientDetails.client)
            : undefined
        }
        onClose={() => setManualFillRow(null)}
        onDraftSaved={handleManualFillDraftSaved}
        onSubmitted={handleManualFillSubmitted}
      />
      <ActionTour
        ariaLabel="Questionnaire responses guide"
        label="Responses guide"
        storageKey="caloriescience:questionnaire-responses-guide:v1"
        steps={questionnaireResponsesActionTourSteps}
      />
    </Box>
  );
};

export default QuestionnaireResponsesPage;
